import type { SelectOption } from '../components/Select';
import { Project } from '../models';

const SEPARATOR: SelectOption = {
    value: '__project_separator__',
    label: '',
    disabled: true,
    separator: true,
};

export function projectHasClient(project: Project, clientId?: string): boolean {
    if (!clientId)
        return false;
    return (project.clients || []).some((c: AnyObj) =>
        (typeof c === 'string' ? c : c?._id) === clientId
    );
}

export function matchingProjectsForClient(projects: Project[] | undefined, clientId?: string): Project[] {
    if (!clientId)
        return [];
    return (projects || []).filter((project) => projectHasClient(project, clientId));
}

/**
 * Project options with client-linked projects first, then a separator, then the rest.
 * Allows picking any project without assigning every client to e.g. Support.
 * When `pinnedProjectId` is set, that project is always present (and listed first).
 */
export function projectSelectOptions(
    projects: Project[] | undefined,
    clientId?: string,
    pinnedProjectId?: string
): SelectOption[] {
    const list = [...(projects || [])].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    );
    const toOption = (project: Project): SelectOption => ({
        value: project._id,
        label: project.name,
    });

    const pinProject = pinnedProjectId
        ? list.find((project) => project._id === pinnedProjectId)
        : undefined;

    const withoutPinned = pinProject
        ? list.filter((project) => project._id !== pinProject._id)
        : list;

    if (!clientId) {
        const options = withoutPinned.map(toOption);
        return pinProject ? [toOption(pinProject), ...options] : options;
    }

    const matching: Project[] = [];
    const others: Project[] = [];
    withoutPinned.forEach((project) => {
        if (projectHasClient(project, clientId))
            matching.push(project);
        else
            others.push(project);
    });

    const rest = (!matching.length || !others.length)
        ? [...matching, ...others].map(toOption)
        : [
            ...matching.map(toOption),
            SEPARATOR,
            ...others.map(toOption),
        ];

    return pinProject ? [toOption(pinProject), ...rest] : rest;
}
